import React, { useCallback, useEffect, useState } from "react";
import { Box, Chip, Grid, Paper, Typography } from "@mui/material";
import BackButton from "../../common/back-button";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_GOAL_PLANS_ENDPOINT } from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtINR = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `₹${Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtYears = (n) =>
  n == null || n === "" ? "—" : `${n} year${Number(n) === 1 ? "" : "s"}`;

const STATUS_CHIP_COLOR = {
  Open:             "info",
  "In Progress":    "warning",
  Converted:        "success",
  "Not Interested": "default",
};

// ─── Field tile ───────────────────────────────────────────────────────────────

const Field = ({ label, value, isStatus }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    {isStatus ? (
      <Box sx={{ mt: 0.5 }}>
        <Chip
          variant="outlined"
          size="small"
          label={value}
          color={STATUS_CHIP_COLOR[value] ?? "default"}
        />
      </Box>
    ) : (
      <Typography variant="body1" sx={{ mt: 0.5 }}>
        {value ?? "—"}
      </Typography>
    )}
  </Box>
);

// ─── Component ────────────────────────────────────────────────────────────────

const GoalPlanDetailView = ({ plan: initialPlan, onBack }) => {
  const { getValidToken } = useAuth();
  const [plan, setPlan] = useState(initialPlan);

  const getHeaders = useCallback(async () => {
    const token  = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [getValidToken]);

  // Refresh from detail endpoint, falling back to the row passed in
  useEffect(() => {
    if (!initialPlan?.id) return;
    (async () => {
      try {
        const headers = await getHeaders();
        const res = await fetch(`${MFD_GOAL_PLANS_ENDPOINT}${initialPlan.id}/`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        setPlan(data);
      } catch {
        // Keep initial row data on error
      }
    })();
  }, [initialPlan?.id, getHeaders]);

  return (
    <Box sx={{ p: 3 }}>
      <BackButton onClick={onBack} />

      <Box sx={{ mt: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>Goal Plan Details</Typography>
        <Typography sx={{ ...typography.bodyLarge, color: "text.secondary", mt: 0.5 }}>
          {plan.client_name}
        </Typography>
      </Box>

      {/* Client */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Client Name" value={plan.client_name || plan.name} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="PAN" value={plan.pan} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Client Code" value={plan.client_code} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Phone Number" value={plan.phone_no} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Email" value={plan.email} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Status" value={plan.status} isStatus />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field
              label="Created At"
              value={plan.created_at ? formatWithTzLabel(plan.created_at) : "—"}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field
              label="Updated At"
              value={plan.updated_at ? formatWithTzLabel(plan.updated_at) : "—"}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Goal */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Goal Planning Details
        </Typography>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Field label="Goal" value={plan.goal} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Field label="Target Amount" value={fmtINR(plan.target_amount)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Field label="Timeline" value={fmtYears(plan.timeline)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Field label="Monthly SIP" value={fmtINR(plan.monthly_sip)} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Field label="Lumpsum" value={fmtINR(plan.lumpsum)} />
            </Grid>
            <Grid item xs={12}>
              <Field label="Comment" value={plan.comment} />
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default GoalPlanDetailView;
